/**
 * Asking the server whether this browser may have the dev tools on a deployed origin.
 *
 * A browser cannot see whether the Wix VPN is on, so the question goes to `/api/dev-tools-access`,
 * which answers from the caller's IP (`lib/dev-tools-network.ts`). This module owns WHEN we ask:
 *
 *  - **On the gesture** (`requestDevToolsAccess`) — only once someone actually holds the brand mark
 *    down for the full long-press. An ordinary click never asks, so a real user's page costs no
 *    extra request at all.
 *  - **On load, only for a tab that already earned it** (`revalidateDevToolsAccess`) — the grant is
 *    cached so the mock layer can read it synchronously, so it has to be re-checked: a stale or
 *    hand-forged cache key must not keep the tools alive once the server would say no.
 *
 * The verdict is memoized per page load (both answers), so the endpoint is hit at most ONCE per
 * load however many times the gesture happens. A probe that never reached a verdict (offline,
 * non-200) is not memoized, so a later press can try again.
 */

import { API_BASE } from './api-base';
import { devToolsAvailable, devToolsUnlocked, rememberDevToolsAccess } from './dev-tools';

let verdict: boolean | undefined;
let inFlight: Promise<boolean> | undefined;

async function askServer(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/api/dev-tools-access`, { credentials: 'include' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const allowed = ((await res.json()) as { allowed?: boolean }).allowed === true;
    verdict = allowed;
    rememberDevToolsAccess(allowed);
    return allowed;
  } catch {
    // No verdict — leave the cached grant (if any) alone and let a later press ask again.
    inFlight = undefined;
    return false;
  }
}

/** Whether the dev tools may open. Asks the server once per page load; instant in a dev build. */
export async function requestDevToolsAccess(): Promise<boolean> {
  if (devToolsAvailable) return true;
  if (verdict !== undefined) return verdict;
  inFlight ??= askServer();
  return inFlight;
}

/**
 * Re-check a grant this tab is already holding. Resolves `false` — WITHOUT a request — when there is
 * nothing cached, which is every real user: they never trigger a call to the endpoint on load.
 */
export async function revalidateDevToolsAccess(): Promise<boolean> {
  if (devToolsAvailable) return true;
  if (!devToolsUnlocked()) return false;
  return requestDevToolsAccess();
}

/** Test seam: forget this page load's memoized verdict. */
export function resetDevToolsAccessCache(): void {
  verdict = undefined;
  inFlight = undefined;
}
